import { produce } from "solid-js/store";
import {
  type SetStreamingStore,
  type StreamingBlockId,
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingMessageId,
  StreamingMessageStatus,
  type StreamingOperationId,
  type StreamingStore,
} from "./streamingTypes";

/**
 * Unloads a chat from the store, including all messages, operations, and blocks
 */
export const cleanupChat = (setStore: SetStreamingStore, chatId: StreamingChatId) => {
  setStore(
    produce((store) => {
      mutatingCleanupMessages(store, store.chats[chatId]?.messages);
      delete store.chats[chatId];
    })
  );
};

/**
 * Unloads every chat the caller isn't rendering. A chat still GENERATING is kept
 * unless `includeGenerating`, since its stream keeps writing into the entry.
 *
 * The keep set is the caller's, never derived from the chat being hydrated. Only
 * one chat is mounted today, but a second surface rendering its own chat would
 * otherwise be evicted mid-render by the first one's load.
 */
export const evictChatsExcept = (
  setStore: SetStreamingStore,
  keep: ReadonlySet<StreamingChatId>,
  opts?: { includeGenerating?: boolean }
) => {
  setStore(
    produce((store) => {
      // Every read stays on the produce draft: reading state.chats outside would
      // subscribe a caller running inside a computation to the whole chat map.
      for (const chatId of Object.keys(store.chats) as StreamingChatId[]) {
        if (keep.has(chatId)) continue;
        if (!opts?.includeGenerating && getChatStatus(store, chatId) === StreamingChatStatus.GENERATING) continue;
        mutatingCleanupMessages(store, store.chats[chatId]?.messages);
        delete store.chats[chatId];
      }
    })
  );
};

/**
 * Unloads a message from the store, including all operations and blocks
 */
export const cleanupMessage = (setStore: SetStreamingStore, messageId: StreamingMessageId) => {
  setStore(
    produce((store) => {
      mutatingCleanupMessages(store, [messageId]);
    })
  );
};

/**
 * Removes a message from a chat's list and cleans up the message, its operations, and blocks.
 * Use after successfully deleting a message via the API.
 */
export const removeMessageFromChat = (
  setStore: SetStreamingStore,
  chatId: StreamingChatId,
  messageId: StreamingMessageId
) => {
  setStore(
    produce((store) => {
      const chat = store.chats[chatId];
      if (chat?.messages) {
        chat.messages = chat.messages.filter((id) => id !== messageId);
      }
      mutatingCleanupMessages(store, [messageId]);
    })
  );
};

/**
 * Computed based on the status of the messages
 */
export const getChatStatus = (store: StreamingStore, chatId: StreamingChatId): StreamingChatStatus => {
  const messages = store.chats[chatId]?.messages;
  if (!messages) {
    return StreamingChatStatus.IDLE;
  }
  // If any message is generating, the chat is generating
  for (const messageId of messages) {
    const message = store.messages[messageId];
    if (message?.status === StreamingMessageStatus.GENERATING) {
      return StreamingChatStatus.GENERATING;
    }
  }
  return StreamingChatStatus.IDLE;
};

// Helpers
const mutatingCleanupBlocks = (store: StreamingStore, blocks?: StreamingBlockId[]) => {
  for (const blockId of blocks ?? []) {
    delete store.blocks[blockId];
  }
};
const mutatingCleanupOperations = (store: StreamingStore, operations?: StreamingOperationId[]) => {
  for (const operationId of operations ?? []) {
    const operation = store.operations[operationId];
    if (operation?.blocks?.length) {
      mutatingCleanupBlocks(store, operation.blocks);
    }
    delete store.operations[operationId];
  }
};
const mutatingCleanupMessages = (store: StreamingStore, messages?: StreamingMessageId[]) => {
  for (const messageId of messages ?? []) {
    const message = store.messages[messageId];
    if (message?.operations?.length) {
      mutatingCleanupOperations(store, message.operations);
    }
    delete store.messages[messageId];
  }
};
